import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography, Box } from "@mui/material";
import AppLayout from "../components/AppLayout";
import StyledButton from "../components/StyledButton";
import { COLORS } from "constants/colors";
import { useUrlParams } from "hooks/useUrlParams";
import { soulBackend } from "backend";

// Constants
const POLLING_INTERVAL = 3000; // 3 seconds
const PAYMENT_STATUS = {
  COMPLETED: "COMPLETED",
};

// Custom hook for payment polling
const usePaymentPolling = (
  merchantPaymentId,
  soulId,
  work,
  lng,
  navigate,
  redirectPage,
  purchaseType,
  ticketKey
) => {
  const [message, setMessage] = useState("決済結果を確認しています");
  const [checking, setChecking] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let attempts = 0;
    const check = async () => {
      if (!merchantPaymentId || !soulId) {
        setMessage("決済情報が見つかりません。購入画面から決済状況を確認してください。");
        return;
      }
      setChecking(true);
      try {
        const { status } = await soulBackend(work).getPaypayStatus(
          merchantPaymentId,
          { soulId, purchaseType, ticketKey }
        );
        if (cancelled) return;
        if (status === PAYMENT_STATUS.COMPLETED) {
          const targetPath = redirectPage
            ? `/${work}/${redirectPage}?soul_id=${soulId}&lng=${lng}`
            : `/${work}/how-to-use?soul_id=${soulId}&lng=${lng}`;
          navigate(targetPath, { replace: true });
          return;
        }
        if (["FAILED", "CANCELED", "CANCELLED", "EXPIRED"].includes(status)) {
          setMessage("この決済は完了していません。PayPayの取引履歴をご確認ください。");
          return;
        }
        if (status !== "CREATED") throw new Error("Unknown payment status");
        setMessage("決済結果を確認しています。追加の購入はせずにお待ちください。");
      } catch (error) {
        if (!cancelled) setMessage("決済結果を確認できませんでした。再購入せず、決済状況を再確認してください。");
        return;
      } finally {
        if (!cancelled) setChecking(false);
      }
      attempts += 1;
      if (attempts >= 20) {
        setMessage("決済結果の反映に時間がかかっています。再購入せず、決済状況を再確認してください。");
        return;
      }
      timer = setTimeout(check, POLLING_INTERVAL);
    };
    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [merchantPaymentId, soulId, work, lng, navigate, redirectPage, purchaseType, ticketKey, retry]);

  return {
    message,
    checking,
    retry: () => setRetry(value => value + 1),
  };
};

// Component for polling content
const PollingContent = ({ message, checking, onRetry }) => (
  <Box
    sx={{
      width: "88%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: COLORS.BACKGROUND_WHITE,
      borderRadius: "5px",
      padding: 4,
      gap: 4,
    }}
  >
    <Typography
      variant="h6"
      sx={(theme) => ({
        whiteSpace: "pre-line",
        fontWeight: "bold",
        textAlign: "center",
        color: COLORS.TEXT_BLACK,
        mt: 1,
      })}
    >
      {message}
    </Typography>

    <Box>
      <Typography
        sx={(theme) => ({
          fontSize: "15px",
          whiteSpace: "pre-line",
          fontWeight: "bold",
          textAlign: "center",
          lineHeight: "1.8",
          color: "red",
          border: "1px solid red",
          borderRadius: "4px",
          padding: "8px",
          mt: 1,
        })}
      >
        PayPayで支払い済みの場合は、再購入しないでください
      </Typography>
    </Box>

    <StyledButton
      variant="contained"
      sx={{
        width: "100%",
        fontSize: "17px",
        color: COLORS.TEXT_BLACK,
        border: `2px solid ${COLORS.TEXT_BLACK}`,
      }}
      onClick={onRetry}
      disabled={checking}
    >
      決済状況を再確認
    </StyledButton>
  </Box>
);

function PayPayPolling({ LayoutComponent = AppLayout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { soulId, work, lng } = useUrlParams();

  const queryParams = new URLSearchParams(location.search);
  const merchantPaymentId = queryParams.get("merchant_payment_id");
  const redirectPage = queryParams.get("redirect_page");
  const purchaseType = queryParams.get("purchase_type");
  const ticketKey = queryParams.get("ticket_key");

  const payment = usePaymentPolling(
    merchantPaymentId,
    soulId,
    work,
    lng,
    navigate,
    redirectPage,
    purchaseType,
    ticketKey
  );

  return (
    <LayoutComponent page="PayPayPolling">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "100vh",
        }}
      >
        <PollingContent message={payment.message} checking={payment.checking} onRetry={payment.retry} />
      </Box>
    </LayoutComponent>
  );
}

export default PayPayPolling;
